import {
	type Control,
	type FieldValues,
	type Path,
	useWatch,
} from "react-hook-form";
import type {
	ClientPortfolioDomainEnum,
	ProductCategoryKeyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { useProducerOptions } from "web_myplann/Portfolio/Helpers/useProducerOptions";
import { useProductOptions } from "web_myplann/Portfolio/Helpers/useProductOptions";
import type { FlatRowSelectOption } from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";

type ProducerProductOptions = {
	producerOptions: FlatRowSelectOption<string>[];
	productOptions: FlatRowSelectOption<string>[];
	isLoadingProducers: boolean;
	isLoadingProducts: boolean;
};

export const useProducerProductOptions = <
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
>(
	control: Control<FormType, TContext, TTransformed>,
	domain: ClientPortfolioDomainEnum,
	productCategory?: ProductCategoryKeyEnum | null,
): ProducerProductOptions => {
	const producerName = useWatch({
		control,
		name: "producer" as Path<FormType>,
	});

	const {
		options: producerOptions,
		idByName,
		isLoading: isLoadingProducers,
	} = useProducerOptions(domain, productCategory ?? undefined);

	const producerId =
		typeof producerName === "string" && producerName
			? idByName.get(producerName)
			: undefined;

	const { options: productOptions, isLoading: isLoadingProducts } =
		useProductOptions(domain, producerId, productCategory ?? undefined);

	return {
		producerOptions,
		productOptions,
		isLoadingProducers,
		isLoadingProducts,
	};
};
